import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  assignAccountingAccountantSalon,
  createAccountingAccountant,
  getAccountingAccountant,
  getAccountingReviewSummary,
  listAccountingAccountants,
  revokeAccountingAccountantSalon,
  sendAccountingAccountantSetupLink,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function statusClass(status) {
  if (status === "active") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "invited") return "border-sky-200 bg-sky-50 text-sky-700";
  if (status === "revoked") return "border-slate-200 bg-slate-50 text-slate-600";
  return "border-amber-200 bg-amber-50 text-amber-700";
}

function assignmentLabel(assignment) {
  return `${assignment.salon_name || "Salon"} / Salon ID ${assignment.salon_id}`;
}

export default function SalonBusinessCpaAccountants() {
  const { token } = useAuth();
  const [accountants, setAccountants] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedAccountant, setSelectedAccountant] = useState(null);
  const [summaryRows, setSummaryRows] = useState([]);
  const [inviteForm, setInviteForm] = useState({ full_name: "", email: "" });
  const [assignmentForm, setAssignmentForm] = useState({
    salon_id: "",
    access_level: "reviewer",
    internal_note: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const selected = selectedAccountant || accountants.find((row) => row.id === selectedId) || null;

  const salonOptions = useMemo(() => {
    return (summaryRows || [])
      .map((row) => ({
        salon_id: row.salon_id,
        label: `${row.salon_name || "Salon"} / Salon ID ${row.salon_id}`,
        business_name: row.business_name || "Business",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [summaryRows]);

  const loadData = async (nextSelectedId = selectedId) => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const [accountantsData, summaryData] = await Promise.all([
        listAccountingAccountants(token),
        getAccountingReviewSummary(token),
      ]);
      const nextAccountants = Array.isArray(accountantsData?.accountants)
        ? accountantsData.accountants
        : [];
      setAccountants(nextAccountants);
      setSummaryRows(Array.isArray(summaryData?.salons) ? summaryData.salons : []);
      const nextId =
        nextSelectedId ||
        selected?.id ||
        nextAccountants.find((row) => row.active_assignment_count > 0)?.id ||
        nextAccountants[0]?.id ||
        null;
      setSelectedId(nextId);
      if (nextId) {
        const detail = await getAccountingAccountant(token, nextId);
        setSelectedAccountant(detail);
      } else {
        setSelectedAccountant(null);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load accountants.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const selectAccountant = async (accountantId) => {
    if (!token || !accountantId) return;
    setSelectedId(accountantId);
    setError("");
    try {
      const detail = await getAccountingAccountant(token, accountantId);
      setSelectedAccountant(detail);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to open this accountant profile.");
    }
  };

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!token) return;
    setSaving("invite");
    setError("");
    setNotice("");
    try {
      const created = await createAccountingAccountant(token, inviteForm);
      setInviteForm({ full_name: "", email: "" });
      setNotice("Accountant profile is ready. A setup link was sent when email delivery was available.");
      await loadData(created?.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to create accountant profile.");
    } finally {
      setSaving("");
    }
  };

  const handleSendSetupLink = async () => {
    if (!token || !selected?.id) return;
    setSaving("setup-link");
    setError("");
    setNotice("");
    try {
      const result = await sendAccountingAccountantSetupLink(token, selected.id);
      setNotice(result?.message || "Setup link sent.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to send the setup link.");
    } finally {
      setSaving("");
    }
  };

  const handleAssign = async (event) => {
    event.preventDefault();
    if (!token || !selected?.id || !assignmentForm.salon_id) return;
    setSaving("assign");
    setError("");
    setNotice("");
    try {
      const updated = await assignAccountingAccountantSalon(token, selected.id, {
        salon_id: Number(assignmentForm.salon_id),
        access_level: assignmentForm.access_level,
        internal_note: assignmentForm.internal_note,
      });
      setSelectedAccountant(updated);
      setAssignmentForm({ salon_id: "", access_level: "reviewer", internal_note: "" });
      setNotice("Salon access has been assigned.");
      await loadData(updated?.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to assign this Salon ID.");
    } finally {
      setSaving("");
    }
  };

  const handleRevoke = async (assignment) => {
    if (!token || !selected?.id || !assignment?.id) return;
    const reason = window.prompt("Reason for removing access, optional") || "";
    setSaving(`revoke-${assignment.id}`);
    setError("");
    setNotice("");
    try {
      const updated = await revokeAccountingAccountantSalon(
        token,
        selected.id,
        assignment.id,
        { revocation_reason: reason }
      );
      setSelectedAccountant(updated);
      setNotice("Salon access has been removed.");
      await loadData(updated?.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to remove this Salon ID access.");
    } finally {
      setSaving("");
    }
  };

  const activeAssignments = (selected?.assignments || []).filter(
    (assignment) => assignment.status === "active"
  );
  const revokedAssignments = (selected?.assignments || []).filter(
    (assignment) => assignment.status === "revoked"
  );

  return (
    <SalonBusinessCpaShell
      title="Accountants"
      description="Invite accountants, assign the Salon IDs they can work on, and review their recent SalonBusinessCPA activity."
      actions={
        <button
          type="button"
          onClick={() => loadData(selected?.id)}
          className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Refresh
        </button>
      }
    >
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {notice}
        </div>
      ) : null}

      <section className="grid gap-4 md:grid-cols-4">
        {[
          ["Accountants", accountants.length],
          [
            "Active access",
            accountants.reduce((total, row) => total + Number(row.active_assignment_count || 0), 0),
          ],
          [
            "Open requests",
            accountants.reduce((total, row) => total + Number(row.open_request_count || 0), 0),
          ],
          [
            "Reviewed records",
            accountants.reduce((total, row) => total + Number(row.documents_reviewed_count || 0), 0),
          ],
        ].map(([label, value]) => (
          <div key={label} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              {label}
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{value}</p>
          </div>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.35fr)]">
        <div className="space-y-6">
          <form
            onSubmit={handleInvite}
            className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-slate-950">Add accountant</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Create the accountant profile first. Then assign the salons they are allowed to work on.
            </p>
            <div className="mt-5 space-y-3">
              <label className="block text-sm font-semibold text-slate-800">
                Name
                <input
                  value={inviteForm.full_name}
                  onChange={(event) =>
                    setInviteForm((current) => ({ ...current, full_name: event.target.value }))
                  }
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Accountant name"
                />
              </label>
              <label className="block text-sm font-semibold text-slate-800">
                Email
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(event) =>
                    setInviteForm((current) => ({ ...current, email: event.target.value }))
                  }
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  placeholder="accountant@example.com"
                />
              </label>
              <button
                type="submit"
                disabled={saving === "invite"}
                className="w-full rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
              >
                {saving === "invite" ? "Saving..." : "Create accountant"}
              </button>
            </div>
          </form>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Accountant list</h2>
            <div className="mt-4 space-y-2">
              {loading ? (
                <p className="text-sm text-slate-500">Loading accountants...</p>
              ) : accountants.length ? (
                accountants.map((accountant) => (
                  <button
                    key={accountant.id}
                    type="button"
                    onClick={() => selectAccountant(accountant.id)}
                    className={`w-full rounded-2xl border p-4 text-left transition ${
                      selected?.id === accountant.id
                        ? "border-rose-300 bg-rose-50"
                        : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-950">
                          {accountant.full_name || accountant.email}
                        </p>
                        <p className="truncate text-xs text-slate-500">{accountant.email}</p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass(
                          accountant.status
                        )}`}
                      >
                        {accountant.status}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-600">
                      <span>{accountant.active_assignment_count || 0} salons</span>
                      <span>{accountant.open_request_count || 0} open</span>
                      <span>{accountant.documents_reviewed_count || 0} reviewed</span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-slate-500">No accountants added yet.</p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            {selected ? (
              <>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Accountant profile
                    </p>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-950">
                      {selected.full_name || selected.email}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">{selected.email}</p>
                  </div>
                  <span
                    className={`inline-flex w-fit rounded-full border px-3 py-1 text-sm font-semibold ${statusClass(
                      selected.status
                    )}`}
                  >
                    {selected.status}
                  </span>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleSendSetupLink}
                    disabled={saving === "setup-link"}
                    className="rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60"
                  >
                    {saving === "setup-link" ? "Sending..." : "Send setup link"}
                  </button>
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-4">
                  {[
                    ["Active salons", selected.active_assignment_count || 0],
                    ["Open requests", selected.open_request_count || 0],
                    ["Assigned open", selected.assigned_open_request_count || 0],
                    ["Reminders", selected.reminders_sent_count || 0],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        {label}
                      </p>
                      <p className="mt-2 text-xl font-semibold text-slate-950">{value}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Last login
                  </p>
                  <p className="mt-2 text-sm font-semibold text-slate-950">
                    {formatDateTime(selected.last_login_utc)}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    {selected.last_login_device || "Device not recorded"}
                    {selected.last_login_ip ? ` / ${selected.last_login_ip}` : ""}
                  </p>
                </div>

                <form
                  onSubmit={handleAssign}
                  className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <h3 className="font-semibold text-slate-950">Assign Salon ID</h3>
                  <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1.4fr)_minmax(0,0.8fr)]">
                    <label className="block text-sm font-semibold text-slate-800">
                      Salon
                      <select
                        required
                        value={assignmentForm.salon_id}
                        onChange={(event) =>
                          setAssignmentForm((current) => ({
                            ...current,
                            salon_id: event.target.value,
                          }))
                        }
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
                      >
                        <option value="">Choose salon</option>
                        {salonOptions.map((salon) => (
                          <option key={salon.salon_id} value={salon.salon_id}>
                            {salon.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block text-sm font-semibold text-slate-800">
                      Access
                      <select
                        value={assignmentForm.access_level}
                        onChange={(event) =>
                          setAssignmentForm((current) => ({
                            ...current,
                            access_level: event.target.value,
                          }))
                        }
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
                      >
                        <option value="reviewer">Review work</option>
                        <option value="read_only">Read only</option>
                      </select>
                    </label>
                  </div>
                  <label className="mt-3 block text-sm font-semibold text-slate-800">
                    Internal note
                    <input
                      value={assignmentForm.internal_note}
                      onChange={(event) =>
                        setAssignmentForm((current) => ({
                          ...current,
                          internal_note: event.target.value,
                        }))
                      }
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
                      placeholder="Optional note for super-admin review"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={saving === "assign" || !assignmentForm.salon_id}
                    className="mt-4 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {saving === "assign" ? "Assigning..." : "Assign salon"}
                  </button>
                </form>
              </>
            ) : (
              <p className="text-sm text-slate-500">Choose an accountant to view their work.</p>
            )}
          </section>

          {selected ? (
            <>
              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Salon access</h2>
                <div className="mt-4 space-y-3">
                  {activeAssignments.length ? (
                    activeAssignments.map((assignment) => (
                      <div
                        key={assignment.id}
                        className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4"
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="font-semibold text-slate-950">
                              {assignmentLabel(assignment)}
                            </p>
                            <p className="mt-1 text-xs text-slate-600">
                              {assignment.business_name || "Business"} / {assignment.access_level}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              Assigned {formatDate(assignment.assigned_at_utc)}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Link
                              to={`/review?business_id=${assignment.business_id}&salon_id=${assignment.salon_id}`}
                              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Review
                            </Link>
                            <Link
                              to={`/data?business_id=${assignment.business_id}&salon_id=${assignment.salon_id}`}
                              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Source data
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleRevoke(assignment)}
                              disabled={saving === `revoke-${assignment.id}`}
                              className="rounded-full border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                            >
                              {saving === `revoke-${assignment.id}` ? "Removing..." : "Remove access"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No active Salon ID access.</p>
                  )}
                </div>
                {revokedAssignments.length ? (
                  <div className="mt-5">
                    <h3 className="text-sm font-semibold text-slate-800">Past access</h3>
                    <div className="mt-2 space-y-2">
                      {revokedAssignments.slice(0, 6).map((assignment) => (
                        <div
                          key={assignment.id}
                          className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600"
                        >
                          {assignmentLabel(assignment)} removed {formatDate(assignment.revoked_at_utc)}
                          {assignment.revocation_reason ? ` - ${assignment.revocation_reason}` : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Recent work</h2>
                <div className="mt-4 space-y-3">
                  {Array.isArray(selected.recent_activity) && selected.recent_activity.length ? (
                    selected.recent_activity.map((activity) => (
                      <div
                        key={activity.id}
                        className="rounded-2xl border border-slate-200 bg-white p-4"
                      >
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="font-semibold text-slate-950">{activity.title}</p>
                            <p className="mt-1 text-xs text-slate-500">
                              {activity.salon_name || "Salon"} / Salon ID {activity.salon_id}
                            </p>
                          </div>
                          <p className="text-xs text-slate-500">
                            {formatDate(activity.occurred_at_utc)}
                          </p>
                        </div>
                        {activity.ip_address || activity.device_label ? (
                          <p className="mt-2 text-xs leading-5 text-slate-500">
                            {activity.device_label || "Device not recorded"}
                            {activity.ip_address ? ` / ${activity.ip_address}` : ""}
                          </p>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No activity logged yet.</p>
                  )}
                </div>
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Login activity</h2>
                <div className="mt-4 space-y-3">
                  {Array.isArray(selected.recent_login_activity) &&
                  selected.recent_login_activity.length ? (
                    selected.recent_login_activity.map((event) => (
                      <div
                        key={event.id}
                        className="rounded-2xl border border-slate-200 bg-white p-4"
                      >
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="font-semibold text-slate-950">
                              {event.event_type === "mfa_completed"
                                ? "MFA completed"
                                : "Signed in"}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {event.device_label || "Device not recorded"}
                              {event.ip_address ? ` / ${event.ip_address}` : ""}
                            </p>
                          </div>
                          <p className="text-xs text-slate-500">
                            {formatDateTime(event.occurred_at_utc)}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No login activity recorded yet.</p>
                  )}
                </div>
              </section>
            </>
          ) : null}
        </div>
      </section>
    </SalonBusinessCpaShell>
  );
}
